import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AlertCircle,
  AlertTriangle,
  Columns2,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search,
  ShieldAlert
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444'
};

const translateTipo = (value) => {
  const map = { verbale: 'Verbale', scritto: 'Scritto' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const TipoChip = ({ tipo }) => {
  const key = String(tipo || '').toLowerCase();
  const style = key === 'scritto' ? { color: UI_COLORS.indigo } : { color: UI_COLORS.amber };
  return (
    <Chip
      label={translateTipo(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize', fontWeight: 700, color: style.color, borderColor: style.color }}
    />
  );
};

const translateStato = (value) => {
  const map = {
    aperta: 'Aperta',
    in_verifica: 'In verifica',
    confermata: 'Confermata',
    archiviata: 'Archiviata',
    respinta: 'Respinta'
  };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const SegnalazioneStatoChip = ({ stato }) => {
  const key = String(stato || '').toLowerCase();
  const style =
    key === 'confermata'
      ? { color: UI_COLORS.green }
      : key === 'respinta'
      ? { color: UI_COLORS.red }
      : key === 'in_verifica'
      ? { color: UI_COLORS.amber }
      : key === 'archiviata'
      ? { color: UI_COLORS.indigo }
      : { color: UI_COLORS.sky };
  return (
    <Chip
      label={translateStato(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize', fontWeight: 700, color: style.color, borderColor: style.color }}
    />
  );
};

const formatDateIt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: formatDateIso(from), to: formatDateIso(to) };
};

export default function BlacklistRichiami() {
  const columnDefs = useMemo(
    () => [
      { key: 'data_richiamo', label: 'Data', sortable: true, align: 'left' },
      { key: 'tipo', label: 'Tipo', sortable: true, align: 'left' },
      { key: 'utente_segnalato_name', label: 'Segnalato', sortable: true, align: 'left' },
      { key: 'segnalazione_id', label: 'Segnalazione', sortable: true, align: 'left' },
      { key: 'note', label: 'Note', sortable: false, align: 'left' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_richiamo');
  const [filterText, setFilterText] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(() => getCurrentMonthRange().from);
  const [filterDateTo, setFilterDateTo] = useState(() => getCurrentMonthRange().to);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'data_richiamo',
    'tipo',
    'utente_segnalato_name',
    'segnalazione_id'
  ]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [accessDialogOpen, setAccessDialogOpen] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);

  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [segnalazioniOptions, setSegnalazioniOptions] = useState([]);
  const [loadingSegnalazioni, setLoadingSegnalazioni] = useState(false);

  const [formData, setFormData] = useState({
    utente_segnalato_id: '',
    tipo: 'verbale',
    data_richiamo: '',
    segnalazione_id: '',
    note: ''
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/blacklist/richiami');
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching blacklist richiami:', err);
      toast.error('Errore nel caricamento dei richiami');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessGranted) return undefined;
    fetchRows();
    return undefined;
  }, [accessGranted]);

  const handleAccessConfirm = () => {
    setAccessDialogOpen(false);
    setAccessGranted(true);
  };

  const handleAccessCancel = () => {
    setAccessDialogOpen(false);
    window.history.back();
  };

  const loadUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const res = await api.get('/users', { params: { page: 1, limit: 500, sort: 'name', order: 'ASC' } });
      const list = res.data?.users || [];
      setUserOptions(Array.isArray(list) ? list : []);
    } catch (_) {
      setUserOptions([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadSegnalazioni = async () => {
    if (loadingSegnalazioni) return;
    setLoadingSegnalazioni(true);
    try {
      const res = await api.get('/blacklist/segnalazioni');
      const list = Array.isArray(res.data) ? res.data : [];
      const normalized = list.slice(0, 300).map((s) => ({
        id: s.id,
        data_segnalazione: s.data_segnalazione || null,
        utente_segnalato_name: s.utente_segnalato_name || '',
        ruolo_segnalato: s.ruolo_segnalato || '',
        stato: s.stato || '',
        note: s.note || '',
        label: `#${s.id} — ${formatDateIt(s.data_segnalazione)} — ${s.utente_segnalato_name || ''}`.trim()
      }));
      setSegnalazioniOptions(normalized);
    } catch (_) {
      setSegnalazioniOptions([]);
    } finally {
      setLoadingSegnalazioni(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const compareText = (a, b) => {
    const sa = String(a || '').toLowerCase();
    const sb = String(b || '').toLowerCase();
    if (sa < sb) return -1;
    if (sa > sb) return 1;
    return 0;
  };

  const filteredRows = rows
    .filter((row) => {
      if (filterTipo && String(row?.tipo || '') !== filterTipo) return false;
      if (filterDateFrom && String(row?.data_richiamo || '') < filterDateFrom) return false;
      if (filterDateTo && String(row?.data_richiamo || '') > filterDateTo) return false;
      if (!filterText) return true;
      const s = filterText.toLowerCase();
      return (
        String(row?.utente_segnalato_name || '').toLowerCase().includes(s) ||
        String(row?.note || '').toLowerCase().includes(s) ||
        String(row?.segnalazione_id || '').toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      const valA = a[orderBy];
      const valB = b[orderBy];
      if (orderBy === 'data_richiamo') {
        const da = valA ? new Date(valA).getTime() : 0;
        const db = valB ? new Date(valB).getTime() : 0;
        return isAsc ? da - db : db - da;
      }
      const cmp = compareText(valA, valB);
      return isAsc ? cmp : -cmp;
    });

  const displayedColumns = columnDefs.filter((col) => visibleColumns.includes(col.key));

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(columnKey);
      if (isVisible && prev.length === 1) return prev;
      if (isVisible) return prev.filter((key) => key !== columnKey);
      return [...prev, columnKey];
    });
  };

  const pickUserOption = (id) => {
    const sid = String(id || '');
    return userOptions.find((u) => String(u.id) === sid) || null;
  };

  const pickSegnalazioneOption = (id) => {
    const sid = String(id || '');
    return segnalazioniOptions.find((s) => String(s.id) === sid) || null;
  };

  const openCreate = () => {
    setCurrentRow(null);
    setFormData({
      utente_segnalato_id: '',
      tipo: 'verbale',
      data_richiamo: '',
      segnalazione_id: '',
      note: ''
    });
    loadUsers();
    loadSegnalazioni();
    setOpenDialog(true);
  };

  const openEdit = async (row) => {
    loadUsers();
    loadSegnalazioni();
    try {
      const res = await api.get(`/blacklist/richiami/${row.id}`);
      const data = res.data || {};
      setCurrentRow(data);
      setFormData({
        utente_segnalato_id: data.utente_segnalato_id ? String(data.utente_segnalato_id) : '',
        tipo: data.tipo || 'verbale',
        data_richiamo: data.data_richiamo ? String(data.data_richiamo).slice(0, 10) : '',
        segnalazione_id: data.segnalazione_id ? String(data.segnalazione_id) : '',
        note: data.note || ''
      });
      setOpenDialog(true);
    } catch (e) {
      console.error('Error loading richiamo:', e);
      toast.error('Impossibile aprire il richiamo');
    }
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setCurrentRow(null);
  };

  const handleSubmit = async () => {
    const payload = {
      utente_segnalato_id: formData.utente_segnalato_id ? Number(formData.utente_segnalato_id) : null,
      tipo: String(formData.tipo || '').trim().toLowerCase(),
      data_richiamo: String(formData.data_richiamo || '').trim(),
      segnalazione_id: formData.segnalazione_id ? Number(formData.segnalazione_id) : null,
      note: String(formData.note || '').trim()
    };

    if (!payload.utente_segnalato_id || !payload.tipo || !payload.data_richiamo) {
      toast.error('Seleziona segnalato, tipo e data');
      return;
    }

    try {
      if (currentRow?.id) {
        await api.put(`/blacklist/richiami/${currentRow.id}`, payload);
        toast.success('Richiamo aggiornato');
      } else {
        await api.post('/blacklist/richiami', payload);
        toast.success('Richiamo creato');
      }
      closeDialog();
      fetchRows();
    } catch (e) {
      console.error('Error saving richiamo:', e);
      toast.error(e?.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/blacklist/richiami/${deleteTarget.id}`);
      toast.success('Richiamo eliminato');
      closeDeleteDialog();
      fetchRows();
    } catch (e) {
      console.error('Error deleting richiamo:', e);
      toast.error(e?.response?.data?.message || 'Errore durante eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Dialog
        open={accessDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleAccessCancel();
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 'bold' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: '#fee2e2',
              color: '#b91c1c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AlertTriangle size={18} />
          </Box>
          Conferma gestione richiami
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {`Stai per accedere alla gestione dei richiami Blacklist. Questa sezione può contenere informazioni sensibili (nominativi, note interne e riferimenti alle segnalazioni).\n\nAssicurati di avere un motivo legittimo e l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={handleAccessCancel}>
            Annulla
          </Button>
          <Button onClick={handleAccessConfirm}>Conferma</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: `${UI_COLORS.red}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ShieldAlert size={20} color={UI_COLORS.red} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Richiami
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tracciamento richiami e recidiva
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openCreate}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo richiamo
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <CardContent>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 340, flex: '0 0 auto' }}>
              <TextField
                placeholder="Cerca per segnalato, note, segnalazione..."
                variant="outlined"
                size="small"
                fullWidth
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filterTipo}
                  label="Tipo"
                  onChange={(e) => {
                    setFilterTipo(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="verbale">Verbale</MenuItem>
                  <MenuItem value="scritto">Scritto</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sx={{ minWidth: 300, flex: '0 0 auto', display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Da"
                InputLabelProps={{ shrink: true }}
                value={filterDateFrom}
                onChange={(e) => {
                  setFilterDateFrom(e.target.value);
                  setPage(0);
                }}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label="A"
                InputLabelProps={{ shrink: true }}
                value={filterDateTo}
                onChange={(e) => {
                  setFilterDateTo(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>

            <Grid item sx={{ minWidth: 180, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outline"
                onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                title="Colonne"
              >
                <Columns2 className="h-4 w-4" />
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
              >
                {columnDefs.map((col) => (
                  <MenuItem key={col.key} onClick={() => handleToggleColumn(col.key)}>
                    <Checkbox checked={visibleColumns.includes(col.key)} />
                    {col.label}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {displayedColumns.map((col) => (
                    <TableCell key={col.key} align={col.align}>
                      {col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleRequestSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 120 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                      Nessun record trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow key={row.id} hover>
                        {displayedColumns.map((col) => {
                          if (col.key === 'data_richiamo') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                {formatDateIt(row.data_richiamo) || 'n/d'}
                              </TableCell>
                            );
                          }
                          if (col.key === 'tipo') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <TipoChip tipo={row.tipo} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'segnalazione_id') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ minWidth: 280 }}>
                                {row.segnalazione_id ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {`#${row.segnalazione_id}`}
                                      {row.segnalazione_data ? ` — ${formatDateIt(row.segnalazione_data)}` : ''}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {row.segnalazione_ruolo ? `Ruolo: ${String(row.segnalazione_ruolo).charAt(0).toUpperCase()}${String(row.segnalazione_ruolo).slice(1)}` : ''}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        Stato:
                                      </Typography>
                                      <SegnalazioneStatoChip stato={row.segnalazione_stato} />
                                    </Box>
                                    {row.segnalazione_note ? (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical'
                                        }}
                                      >
                                        {row.segnalazione_note}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            );
                          }
                          if (col.key === 'note') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ maxWidth: 380 }}>
                                <Typography variant="body2" className="truncate">
                                  {row.note || ''}
                                </Typography>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                              {row[col.key] ?? ''}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon className="h-4 w-4 text-slate-900 dark:text-slate-100" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDeleteDialog(row)}>
                            <DeleteIcon className="h-4 w-4 text-red-500" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentRow?.id ? 'Modifica richiamo' : 'Nuovo richiamo'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={userOptions}
                loading={loadingUsers}
                value={pickUserOption(formData.utente_segnalato_id)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                getOptionLabel={(o) => (o?.name ? String(o.name) : '')}
                onChange={(_e, v) => setFormData((p) => ({ ...p, utente_segnalato_id: v?.id ? String(v.id) : '' }))}
                renderInput={(params) => <TextField {...params} label="Segnalato" placeholder="Seleziona utente" />}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={formData.tipo} label="Tipo" onChange={(e) => setFormData((p) => ({ ...p, tipo: e.target.value }))}>
                  <MenuItem value="verbale">Verbale</MenuItem>
                  <MenuItem value="scritto">Scritto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Data richiamo"
                InputLabelProps={{ shrink: true }}
                value={formData.data_richiamo}
                onChange={(e) => setFormData((p) => ({ ...p, data_richiamo: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={segnalazioniOptions}
                loading={loadingSegnalazioni}
                value={pickSegnalazioneOption(formData.segnalazione_id)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                getOptionLabel={(o) => (o?.label ? String(o.label) : '')}
                onChange={(_e, v) => setFormData((p) => ({ ...p, segnalazione_id: v?.id ? String(v.id) : '' }))}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                      {`#${option.id}`} {option.data_segnalazione ? `— ${formatDateIt(option.data_segnalazione)}` : ''}
                    </Typography>
                    {option.utente_segnalato_name ? (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {option.utente_segnalato_name}
                      </Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                      {option.ruolo_segnalato ? (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {String(option.ruolo_segnalato).charAt(0).toUpperCase()}
                          {String(option.ruolo_segnalato).slice(1)}
                        </Typography>
                      ) : null}
                      {option.stato ? <SegnalazioneStatoChip stato={option.stato} /> : null}
                    </Box>
                    {option.note ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {option.note}
                      </Typography>
                    ) : null}
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Segnalazione (opzionale)" placeholder="Collega a segnalazione" />}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Note"
                size="small"
                fullWidth
                multiline
                minRows={4}
                value={formData.note}
                onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))}
                placeholder="Dettagli del richiamo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Elimina richiamo</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: `${UI_COLORS.red}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AlertCircle size={18} color={UI_COLORS.red} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>{deleteTarget?.id ? `#${deleteTarget.id}` : 'n/d'}</Typography>
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {deleteTarget?.utente_segnalato_name ? `Segnalato: ${deleteTarget.utente_segnalato_name}` : ''}
                </Typography>
              </Box>
            </Box>
            <Paper variant="outlined" elevation={0} sx={{ p: 1.5, borderRadius: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  gap: 0.75,
                  alignItems: 'center'
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Data
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {formatDateIt(deleteTarget?.data_richiamo) || 'n/d'}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Tipo
                </Typography>
                <Box>{deleteTarget?.tipo ? <TipoChip tipo={deleteTarget.tipo} /> : '-'}</Box>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Segnalazione
                </Typography>
                {deleteTarget?.segnalazione_id ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                      {`#${deleteTarget.segnalazione_id}`}
                      {deleteTarget.segnalazione_data ? ` — ${formatDateIt(deleteTarget.segnalazione_data)}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }} noWrap>
                        Stato:
                      </Typography>
                      <SegnalazioneStatoChip stato={deleteTarget.segnalazione_stato} />
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                    —
                  </Typography>
                )}

                {deleteTarget?.note ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      Note
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }} className="truncate">
                      {deleteTarget.note}
                    </Typography>
                  </>
                ) : null}
              </Box>
            </Paper>
            <Typography variant="body2" color="text.secondary">
              Questa operazione non è reversibile.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
